import { CalendarPickerInputDropdown } from "@smartsuite/react-ui/lib";
import type { CalendarPickerClearIcon } from "@smartsuite/react-ui/lib/components/forms/CalendarPickerGroupInput/CalendarPickerGroupInput";
import { enUS } from "date-fns/locale";
import React, { type FC, useMemo } from "react";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormField } from "../../../../hooks/useFormField";
import type { FormFieldProps } from "../../../types";
import type { DateFieldValue } from "../date.config";

import "../date.sass";
import { GrayColors } from "@smartsuite/types";
import * as yup from "yup";
import { useFormContext } from "../../../../hooks/useFormContext";
import { $t } from "../../../../utils/intl";
import { validateWithYup } from "../../../validator";
import { validatorText } from "../../../validator.text";
import {
  dateValueToPickerDate,
  formatDateFieldValue,
  pickerDateToDateValue,
} from "../dateTimezone";

type DateFieldControlProps = FormFieldProps;

const clearIcon: CalendarPickerClearIcon = {
  name: "close-thin",
  size: "S",
  color: GrayColors["G-8"],
};

export const maxYearForSelectOffset = 50;
export const minYearForSelect = 1500;
export const maxYearForSelect = (): number => {
  return new Date().getFullYear() + maxYearForSelectOffset;
};

export const DateFieldControl: FC<DateFieldControlProps> = ({
  name,
  label,
  context,
  caption,
  required,
  formItem,
  readOnly,
}) => {
  const shouldShowTimePicker = useMemo(() => formItem.params?.include_time, [formItem.params]);
  const { preferences } = useFormContext();
  const timezone = preferences.timezone.value;

  const formField = useFormField<DateFieldValue>({
    name,
    validate: validateWithYup(
      yup.object({
        date: yup.mixed().when([], {
          is: () => formItem.required,
          then: (schema) => schema.required($t(validatorText.validationRequired)),
          otherwise: (schema) => schema,
        }),
        include_time: yup.boolean(),
      }),
    ),
  });

  const timeFormat = useMemo(
    () => context.config.form_state.time_format ?? "24h",
    [context.config.form_state.time_format],
  );

  const handleChange = (date: Date, includeTime: boolean): void => {
    formField.onChange(pickerDateToDateValue(date, includeTime, timezone));
  };

  const maxYear = useMemo(() => maxYearForSelect(), []);

  const formattedValue = useMemo(
    () => formatDateFieldValue(formField.value, timeFormat, timezone),
    [formField.value, timeFormat, timezone],
  );

  const pickerValue = useMemo(
    () => dateValueToPickerDate(formField.value, timezone),
    [formField.value, timezone],
  );

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      label={label}
      state={formField.state}
      caption={caption}
      helpText={formItem.params.helpText}
      required={required}
      errorMessage={formField.errorMessage}
      readOnly={readOnly}
    >
      <CalendarPickerInputDropdown
        className="form-date-field"
        inputBackground="white-new"
        clearIcon={clearIcon}
        value={pickerValue}
        formattedValue={formattedValue}
        yearsOptionStart={minYearForSelect}
        yearsOptionEnd={maxYear}
        placeholder="Select Date"
        locale={enUS}
        isClearable={!readOnly}
        showArrow={false}
        timeFormat={timeFormat}
        showTimePicker={shouldShowTimePicker}
        onDateChange={handleChange}
        showIcon={!readOnly}
      />
    </FormFieldControl>
  );
};
